/* ====================================================================
   FYP PATH PAIRINGS — behaviour (v1.4, 18 Jul 2026)
   - Quick add: single-variant one-tap; multi-variant requires an explicit
     selection in the native select (never auto-adds a size).
   - After add: dispatches Impact's cart:refresh AND swaps the cart-drawer
     section via the Section Rendering API (consecutive adds proved
     cart:refresh alone can be missed while the drawer is open), then
     re-opens the drawer (attribute + show() where available).
   - Analytics: impression / click / quick_add via Shopify.analytics.publish
     when available, mirrored as 'fyp-pp:event' CustomEvents. No PII.
   - v1.4: accordion anchor guard — when a details/accordion inside the
     product info column toggles, its header is held at its current
     viewport position for the duration of the animation, so panels only
     ever expand DOWNWARD (Will, 18 Jul: pre-existing jump on some
     viewports where the sticky column repositions on height change).
     No-op when nothing drifts.
   Idempotent: guards against double-binding when both surfaces load it.
==================================================================== */
(function () {
  if (window.__fypPP) return;
  window.__fypPP = true;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';

  function emit(name, detail) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, detail);
      }
    } catch (e) { /* analytics must never break the store */ }
    try {
      document.dispatchEvent(new CustomEvent('fyp-pp:event', { detail: Object.assign({ event: name }, detail) }));
    } catch (e) { /* no-op */ }
  }

  function cardMeta(card) {
    var wrap = card.closest('[data-pp-surface]') || {};
    var ds = wrap.dataset || {};
    return {
      surface: ds.ppSurface || '',
      anchor_product_id: ds.ppAnchor || null,
      season: ds.ppSeason || '',
      recommended_product_id: card.dataset.ppRec,
      need_state: card.dataset.ppNeed,
      source: card.dataset.ppSrc,
      position: card.dataset.ppPos,
      score: card.dataset.ppScore
    };
  }

  /* ---- impressions (once per card element per page load) ---- */
  var seen = new WeakSet();
  var io = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      if (en.isIntersecting && !seen.has(en.target)) {
        seen.add(en.target);
        emit('fyp_pp_impression', cardMeta(en.target));
        io.unobserve(en.target);
      }
    });
  }, { threshold: 0.5 }) : null;

  function observeAll(scope) {
    (scope || document).querySelectorAll('.fyp-pp__card').forEach(function (c) {
      if (io && !seen.has(c)) io.observe(c);
    });
  }
  observeAll();
  new MutationObserver(function () { observeAll(); }).observe(document.body, { childList: true, subtree: true });

  /* ---- v1.4 accordion anchor guard (product info column only) ---- */
  document.addEventListener('toggle', function (ev) {
    var d = ev.target;
    if (!d || d.tagName !== 'DETAILS') return;
    if (!d.closest('safe-sticky, .product-info')) return;
    if (d.closest('#cart-drawer')) return;
    var head = d.querySelector('summary') || d;
    var startTop = head.getBoundingClientRect().top;
    var t0 = performance.now();
    (function hold() {
      var drift = head.getBoundingClientRect().top - startTop;
      if (drift > 0.5 || drift < -0.5) window.scrollBy(0, drift);
      if (performance.now() - t0 < 700) requestAnimationFrame(hold);
    })();
  }, true);

  /* ---- drawer refresh: native event + section swap, then reopen ---- */
  function refreshDrawer(openAfter) {
    try {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (e) { /* no-op */ }
    fetch(root + '?sections=cart-drawer', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        if (!j || !j['cart-drawer']) return;
        var drawer = document.getElementById('cart-drawer');
        if (!drawer) return;
        var tpl = document.createElement('template');
        tpl.innerHTML = j['cart-drawer'];
        var fresh = tpl.content.querySelector('#cart-drawer');
        if (!fresh) return;
        var wasOpen = drawer.hasAttribute('open') || openAfter;
        drawer.replaceWith(fresh);
        var count = document.querySelector('cart-count');
        var freshCount = fresh.querySelector('cart-count');
        if (count && freshCount) count.textContent = freshCount.textContent;
        if (wasOpen) {
          setTimeout(function () {
            try {
              if (typeof fresh.show === 'function') fresh.show();
              else fresh.setAttribute('open', '');
            } catch (e) {
              try { fresh.setAttribute('open', ''); } catch (e2) { /* no-op */ }
            }
          }, 60);
        }
      })
      .catch(function () { /* native event already fired */ });
  }

  /* ---- interactions (event delegation survives re-renders) ---- */
  document.addEventListener('change', function (ev) {
    var sel = ev.target.closest && ev.target.closest('[data-pp-select]');
    if (!sel) return;
    var btn = sel.parentElement.querySelector('[data-pp-add-selected]');
    if (btn) btn.disabled = !sel.value;
  });

  document.addEventListener('click', function (ev) {
    var link = ev.target.closest && ev.target.closest('[data-pp-link]');
    if (link) {
      var lcard = link.closest('.fyp-pp__card');
      if (lcard) emit('fyp_pp_click', cardMeta(lcard));
      return;
    }
    var one = ev.target.closest && ev.target.closest('[data-pp-add]');
    var chosen = ev.target.closest && ev.target.closest('[data-pp-add-selected]');
    if (!one && !chosen) return;

    var btn = one || chosen;
    var card = btn.closest('.fyp-pp__card');
    var surfaceEl = btn.closest('[data-pp-surface]');
    var variantId = one ? btn.dataset.ppAdd : (card.querySelector('[data-pp-select]') || {}).value;
    if (!variantId) return;

    ev.preventDefault();
    btn.setAttribute('data-pp-busy', '');
    btn.disabled = true;

    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
    }).then(function (res) {
      if (!res.ok) return res.json().then(function (j) { throw new Error(j.description || 'Could not add to cart'); });
      return res.json();
    }).then(function () {
      emit('fyp_pp_quick_add', Object.assign(cardMeta(card), { variant_id: variantId }));
      var status = surfaceEl && surfaceEl.querySelector('[data-pp-status]');
      if (status) status.textContent = 'Added to cart';
      refreshDrawer(true);
    }).catch(function (err) {
      var status = surfaceEl && surfaceEl.querySelector('[data-pp-status]');
      if (status) status.textContent = err.message || 'Sorry, that could not be added.';
      btn.disabled = false;
    }).finally(function () {
      btn.removeAttribute('data-pp-busy');
      if (one) btn.disabled = false;
    });
  });
})();